import { ArrowLeftRight } from "lucide-react";
import { DestinationDropdown } from "./DestinationDropdown";
import { useEffect, useState } from "react";
import axios from "axios";
import type { FlightPlaces, FlightSearchRequest } from "@/types/flights";
import { useLocale } from "next-intl";
import { useTranslations } from "use-intl";
import {
  getLocationFromLocalStorage,
  setLocationToLocalStorage,
} from "@/app/utils/services/service/localStorageService";

type Props = {
  from: string;
  to: string;
  withSwitch?: boolean;
  updateFlightField: (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => void;
  flightId: string;
  index?: number;
};

const DestinationField = ({
  from,
  to,
  withSwitch = false,
  updateFlightField,
  flightId,
  index,
}: Props) => {
  const locale = useLocale();
  const t = useTranslations();
  const [searchValueFrom, setSearchValueFrom] = useState("");
  const [searchValueTo, setSearchValueTo] = useState("");
  const [optionsFrom, setOptionsFrom] = useState<FlightPlaces[]>([]);
  const [optionsTo, setOptionsTo] = useState<FlightPlaces[]>([]);
  const [selectedValueTo, setSelectedValueTo] = useState<FlightPlaces | null>(
    null
  );
  const [selectedValueFrom, setSelectedValueFrom] =
    useState<FlightPlaces | null>(null);

  const fetchDataFrom = async (queryParams: string) => {
    try {
      const res = await axios.get(
        `/api/flights/places?query=${queryParams}&lang=${locale}`
      );

      const ops = res?.data?.flights.slice(0, 5).map((flight: any) => ({
        name: flight?.name,
        iataCode: flight?.iataCode,
        city: flight?.city,
        timeZone: flight?.timeZone,
        type: flight?.type,
        country: flight?.country,
      }));

      setOptionsFrom([...ops]);
    } catch (er) {}
  };

  const fetchDataTo = async (queryParams: string) => {
    try {
      const res = await axios.get(
        `/api/flights/places?query=${queryParams}&lang=${locale}`
      );

      const ops = res?.data?.flights.slice(0, 5).map((flight: any) => ({
        name: flight?.name,
        iataCode: flight?.iataCode,
        city: flight?.city,
        timeZone: flight?.timeZone,
        type: flight?.type,
        country: flight?.country,
      }));

      setOptionsTo([...ops]);
    } catch (er) {}
  };

  useEffect(() => {
    const saved = getLocationFromLocalStorage(index?.toString() ?? "", locale);
    if (saved) {
      setSearchValueFrom(saved.from.name);
      setSelectedValueFrom(saved.from);
      updateFlightField(flightId, { from: saved.from.iataCode });

      setSearchValueTo(saved.to.name);
      setSelectedValueTo(saved.to);
      updateFlightField(flightId, { to: saved.to.iataCode });
      fetchDataFrom(saved.from.name);
      fetchDataTo(saved.to.name);
    }
  }, []);

  useEffect(() => {
    let getData: any = "";
    if (searchValueFrom && searchValueFrom != selectedValueFrom?.name) {
      getData = setTimeout(() => {
        fetchDataFrom(searchValueFrom);
      }, 500);
    }

    return () => {
      getData && clearTimeout(getData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueFrom]);

  useEffect(() => {
    let getData: any = "";
    if (searchValueTo && searchValueTo != selectedValueTo?.name) {
      getData = setTimeout(() => {
        fetchDataTo(searchValueTo);
      }, 500);
    }

    return () => {
      getData && clearTimeout(getData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueTo]);

  const handleClickTo = ({ name, iataCode }: FlightPlaces) => {
    setSelectedValueTo({ name, iataCode });
    updateFlightField(flightId, { to: iataCode });
    setSearchValueTo(name);
    if (selectedValueFrom) {
      setLocationToLocalStorage(
        index?.toString() ?? "",
        selectedValueFrom,
        {
          name,
          iataCode,
        },
        locale
      );
    }
  };

  const handleClickFrom = ({ name, iataCode }: FlightPlaces) => {
    setSelectedValueFrom({ name, iataCode });
    updateFlightField(flightId, { from: iataCode });
    setSearchValueFrom(name);
    if (selectedValueTo) {
      setLocationToLocalStorage(
        flightId,
        selectedValueFrom,
        {
          name,
          iataCode,
        },
        locale
      );
    }
  };

  const handleSwitch = () => {
    handleClickFrom(selectedValueTo!);
    handleClickTo(selectedValueFrom!);
  };

  return (
    <div className="flex flex-col gap-2 md:gap-0 md:flex-row md:border md:border-sleap-gray-400 rounded-lg relative md:items-center">
      <DestinationDropdown
        onChange={setSearchValueFrom}
        onClick={handleClickFrom}
        options={optionsFrom}
        value={searchValueFrom}
        otherValue={searchValueTo}
        label={t("HomePageFlights.from")}
      />
      {withSwitch && (
        <button
          onClick={handleSwitch}
          className="z-10 p-3 md:p-2 absolute top-1/2 -translate-y-1/2 -translate-x-1/2 right-0 rounded-full bg-sleap-purple-300 text-white w-13 h-13 md:relative md:-translate-x-0 md:-translate-y-0 shrink-0 md:bg-transparent md:text-sleap-gray-950"
          type="button"
        >
          <ArrowLeftRight className="-rotate-90 md:rotate-0 shrink-0 w-5 h-5 md:w-auto md:h-auto" />
        </button>
      )}
      <DestinationDropdown
        onChange={setSearchValueTo}
        onClick={handleClickTo}
        options={optionsTo}
        value={searchValueTo}
        otherValue={searchValueFrom}
        label={t("HomePageFlights.to")}
      />
    </div>
  );
};

export default DestinationField;
